import { useEffect, useMemo, useState } from "react";
import { Download, Loader2, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { useImportaTargetareLista } from "@/hooks/api/mutate/useImportaTargetareLista";
import { usePreiaTargetareLista } from "@/hooks/api/mutate/usePreiaTargetareLista";
import type {
  TargetareImportListaRezultat,
  TargetareListaFisier,
  TargetareMetodaSursa,
  TargetareServiciuScurt,
  TargetareTipLista,
} from "@/types/targetare";
import {
  ETICHETE_TIP_LISTA,
  numeFisierDinSursa,
} from "@/utils/targetareConfig";
import { descarcaFisierText, formatNumar } from "@/utils/targetareFormatting";
import {
  csvNepotrivite,
  decodeazaBase64,
  decodeazaText,
  deduceColoane,
  detecteazaTipFisier,
  extrageFirmeDinText,
  parseazaCsvText,
  parseazaHtml,
  parseazaPdf,
  parseazaXlsx,
  randuriPentruImport,
  parseazaDocx,
  valoriDistincte,
  type MapareColoane,
  type TabelParsat,
  type TipFisier,
} from "@/utils/targetareListeParsare";
import { SelectServiciu } from "./CelulaLista";

export type ImportListaInitial = {
  nume_fisier?: string;
  url?: string | null;
  service_id?: string | null;
  tip?: TargetareTipLista;
  inlocuieste?: boolean;
  metoda?: TargetareMetodaSursa | null;
  pagina_token?: string | null;
  campuri?: Record<string, string> | null;
  coloana_serviciu?: string | null;
  mapare_serviciu?: Record<string, string> | null;
};

type ImportListaDialogProps = {
  deschis: boolean;
  onClose: () => void;
  fisiere: TargetareListaFisier[];
  servicii: TargetareServiciuScurt[];
  initial?: ImportListaInitial | null;
  onVersiune?: (versiuneId: number) => void;
};

type Sursa = "fisier" | "url" | "text";
const LISTA_NOUA = "__noua__";
const NICIUNA = "__niciuna__";
const FARA_SERVICIU = "__fara__";
const LOT = 500;
const MAX_VALORI_SERVICIU = 60;

type Incarcat = {
  buffer: ArrayBuffer;
  nume: string;
  tip: TipFisier;
  contentType: string | null;
  url?: string;
};

const REZULTAT_GOL: TargetareImportListaRezultat = {
  versiune_id: 0,
  inserate: 0,
  duplicate: 0,
  nepotrivite: 0,
  randuri_nepotrivite: [],
  adaugate: 0,
  eliminate: 0,
  neschimbate: 0,
  final: true,
};

async function parseazaIncarcat(
  inc: Incarcat,
  foaie: string | null,
  extras: boolean
): Promise<TabelParsat> {
  switch (inc.tip) {
    case "xlsx":
      return parseazaXlsx(inc.buffer, foaie);
    case "csv":
      return parseazaCsvText(decodeazaText(inc.buffer));
    case "pdf":
      return parseazaPdf(inc.buffer);
    case "docx":
      return parseazaDocx(inc.buffer);
    case "html": {
      const html = decodeazaText(inc.buffer);
      if (extras) {
        const doc = new DOMParser().parseFromString(html, "text/html");
        return extrageFirmeDinText(doc.body?.innerText ?? "");
      }
      return parseazaHtml(html);
    }
    default: {
      const text = decodeazaText(inc.buffer);
      const linii = text.split(/\r?\n/).filter((l) => l.trim() !== "");
      const cuDelimitatori = linii.filter((l) => /[;,\t^|]/.test(l)).length;
      return !extras && cuDelimitatori >= linii.length * 0.6
        ? parseazaCsvText(text)
        : extrageFirmeDinText(text);
    }
  }
}

function SelectColoana({
  eticheta,
  antet,
  valoare,
  onChange,
}: {
  eticheta: string;
  antet: string[];
  valoare: number | null;
  onChange: (i: number | null) => void;
}) {
  return (
    <label className="space-y-1 text-xs">
      <span className="text-muted-foreground">{eticheta}</span>
      <Select
        value={valoare === null ? NICIUNA : String(valoare)}
        onValueChange={(v) => onChange(v === NICIUNA ? null : Number(v))}
      >
        <SelectTrigger className="h-8 w-[220px] text-xs">
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="max-h-72">
          <SelectItem value={NICIUNA}>(niciuna)</SelectItem>
          {antet.map((h, i) => (
            <SelectItem key={`${i}-${h}`} value={String(i)}>
              {h}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </label>
  );
}

export function ImportListaDialog({
  deschis,
  onClose,
  fisiere,
  servicii,
  initial,
  onVersiune,
}: ImportListaDialogProps) {
  const preia = usePreiaTargetareLista();
  const importa = useImportaTargetareLista();
  const [sursa, setSursa] = useState<Sursa>("fisier");
  const [url, setUrl] = useState("");
  const [text, setText] = useState("");
  const [incarcat, setIncarcat] = useState<Incarcat | null>(null);
  const [foaie, setFoaie] = useState<string | null>(null);
  const [extras, setExtras] = useState(false);
  const [tabel, setTabel] = useState<TabelParsat | null>(null);
  const [mapare, setMapare] = useState<MapareColoane>({
    cui: null,
    denumire: null,
    cod_inmatriculare: null,
  });
  const [coloanaServiciu, setColoanaServiciu] = useState<number | null>(null);
  const [mapareServiciu, setMapareServiciu] = useState<Record<string, string>>(
    {}
  );
  const [listaTinta, setListaTinta] = useState<string>(LISTA_NOUA);
  const [numeNou, setNumeNou] = useState("");
  const [serviceId, setServiceId] = useState<string | null>(null);
  const [tip, setTip] = useState<TargetareTipLista>("NEOFICIALA");
  const [inlocuieste, setInlocuieste] = useState(false);
  const [parsare, setParsare] = useState(false);
  const [eroare, setEroare] = useState<string | null>(null);
  const [progres, setProgres] = useState<{
    trimise: number;
    total: number;
  } | null>(null);
  const [rezultat, setRezultat] = useState<TargetareImportListaRezultat | null>(
    null
  );

  useEffect(() => {
    if (!deschis) return;
    setSursa(initial?.url ? "url" : "fisier");
    setUrl(initial?.url ?? "");
    setText("");
    setIncarcat(null);
    setFoaie(null);
    setExtras(false);
    setTabel(null);
    setEroare(null);
    setProgres(null);
    setRezultat(null);
    setColoanaServiciu(null);
    setMapareServiciu(initial?.mapare_serviciu ?? {});
    const existent = initial?.nume_fisier
      ? fisiere.find((f) => f.nume_fisier === initial.nume_fisier)
      : undefined;
    setListaTinta(existent ? existent.nume_fisier : LISTA_NOUA);
    setNumeNou("");
    setServiceId(existent?.service_id ?? initial?.service_id ?? null);
    setTip(existent?.tip ?? initial?.tip ?? "NEOFICIALA");
    setInlocuieste(initial?.inlocuieste ?? Boolean(existent));
  }, [deschis, initial, fisiere]);

  const listaExistenta = useMemo(
    () => fisiere.find((f) => f.nume_fisier === listaTinta) ?? null,
    [fisiere, listaTinta]
  );

  useEffect(() => {
    if (listaExistenta) {
      setServiceId(listaExistenta.service_id);
      setTip(listaExistenta.tip);
      if (listaExistenta.mapare_serviciu) {
        setMapareServiciu(listaExistenta.mapare_serviciu);
      }
    }
  }, [listaExistenta]);

  const numeColoanaServiciu =
    initial?.coloana_serviciu ?? listaExistenta?.coloana_serviciu ?? null;

  const aplicaParsare = async (
    inc: Incarcat,
    f: string | null,
    ex: boolean
  ) => {
    setParsare(true);
    setEroare(null);
    try {
      const t = await parseazaIncarcat(inc, f, ex);
      setTabel(t);
      setFoaie(t.foaie ?? null);
      setMapare(deduceColoane(t.antet, t.randuri));
      const idx = numeColoanaServiciu
        ? t.antet.findIndex(
            (h) => h.trim().toLowerCase() === numeColoanaServiciu.toLowerCase()
          )
        : -1;
      setColoanaServiciu(idx >= 0 ? idx : null);
      if (listaTinta === LISTA_NOUA && numeNou === "") {
        setNumeNou(
          numeFisierDinSursa(
            inc.nume,
            t.foi && t.foi.length > 1 ? t.foaie : null
          )
        );
      }
    } catch (e) {
      setEroare(
        e instanceof Error ? e.message : "Fișierul nu a putut fi citit"
      );
      setTabel(null);
    } finally {
      setParsare(false);
    }
  };

  const alegeFisier = async (f: File | undefined) => {
    if (!f) return;
    const inc: Incarcat = {
      buffer: await f.arrayBuffer(),
      nume: f.name,
      tip: detecteazaTipFisier(f.name, f.type),
      contentType: f.type,
    };
    setIncarcat(inc);
    await aplicaParsare(inc, null, false);
  };

  const metodaSursa: TargetareMetodaSursa =
    initial?.metoda === "POST" && initial.url === url.trim() ? "POST" : "GET";

  const preiaUrl = () => {
    if (!url.trim()) return;
    preia.mutate(
      {
        url: url.trim(),
        metoda: metodaSursa,
        campuri: metodaSursa === "POST" ? (initial?.campuri ?? {}) : null,
        pagina_token: metodaSursa === "POST" ? initial?.pagina_token : null,
      },
      {
        onSuccess: async (r) => {
          const inc: Incarcat = {
            buffer: decodeazaBase64(r.data.base64),
            nume: r.data.nume,
            tip: detecteazaTipFisier(r.data.nume, r.data.content_type),
            contentType: r.data.content_type,
            url: r.data.url_final,
          };
          setIncarcat(inc);
          await aplicaParsare(inc, null, false);
        },
        onError: (e) => setEroare(e.message),
      }
    );
  };

  const foloseste = async () => {
    const inc: Incarcat = {
      buffer: new TextEncoder().encode(text).buffer as ArrayBuffer,
      nume: "text-lipit.txt",
      tip: "text",
      contentType: "text/plain",
    };
    setIncarcat(inc);
    await aplicaParsare(inc, null, extras);
  };

  const schimbaFoaia = async (f: string) => {
    if (!incarcat) return;
    await aplicaParsare(incarcat, f, extras);
  };
  const schimbaExtras = async (ex: boolean) => {
    setExtras(ex);
    if (incarcat) await aplicaParsare(incarcat, foaie, ex);
  };

  const valoriServiciu = useMemo(
    () => (tabel ? valoriDistincte(tabel, coloanaServiciu) : []),
    [tabel, coloanaServiciu]
  );
  const preaMulteValori = valoriServiciu.length > MAX_VALORI_SERVICIU;
  const mapareActiva =
    coloanaServiciu !== null && !preaMulteValori && valoriServiciu.length > 0;

  const randuri = useMemo(
    () =>
      tabel
        ? randuriPentruImport(
            tabel,
            mapare,
            mapareActiva
              ? { coloana: coloanaServiciu, valori: mapareServiciu }
              : null
          )
        : [],
    [tabel, mapare, mapareActiva, coloanaServiciu, mapareServiciu]
  );
  const cuCui = randuri.filter((r) => r.cui).length;
  const faraServiciu = mapareActiva
    ? randuri.filter((r) => !r.service_id).length
    : 0;
  const numeFinal = listaTinta === LISTA_NOUA ? numeNou.trim() : listaTinta;
  const poateImporta =
    randuri.length > 0 &&
    numeFinal !== "" &&
    !parsare &&
    progres === null &&
    (mapare.cui !== null || mapare.denumire !== null) &&
    (mapareActiva || serviceId !== null || listaExistenta !== null);

  const ruleaza = async () => {
    setEroare(null);
    const loturi: (typeof randuri)[] = [];
    for (let i = 0; i < randuri.length; i += LOT)
      loturi.push(randuri.slice(i, i + LOT));
    if (loturi.length === 0) loturi.push([]);
    setProgres({ trimise: 0, total: randuri.length });
    let versiuneId: number | null = null;
    const agg: TargetareImportListaRezultat = {
      ...REZULTAT_GOL,
      randuri_nepotrivite: [],
    };
    const numeColoana =
      mapareActiva && tabel && coloanaServiciu !== null
        ? tabel.antet[coloanaServiciu]
        : null;
    try {
      for (let i = 0; i < loturi.length; i++) {
        const r = await importa.mutateAsync({
          nume_fisier: numeFinal,
          randuri: loturi[i],
          service_id: mapareActiva ? "" : serviceId,
          tip,
          inlocuieste: i === 0 ? inlocuieste : false,
          final: i === loturi.length - 1,
          sursa:
            sursa === "url"
              ? "url"
              : sursa === "text"
                ? extras
                  ? "crawl"
                  : "text"
                : "import",
          sursa_url: incarcat?.url ?? (sursa === "url" ? url.trim() : null),
          versiune_id: versiuneId,
          coloana_serviciu: i === 0 ? numeColoana : null,
          mapare_serviciu: i === 0 && mapareActiva ? mapareServiciu : null,
          foaie: i === 0 ? (tabel?.foaie ?? null) : null,
          mapare_coloane:
            i === 0 && tabel
              ? {
                  cui: mapare.cui === null ? null : tabel.antet[mapare.cui],
                  denumire:
                    mapare.denumire === null
                      ? null
                      : tabel.antet[mapare.denumire],
                  cod_inmatriculare:
                    mapare.cod_inmatriculare === null
                      ? null
                      : tabel.antet[mapare.cod_inmatriculare],
                }
              : null,
        });
        versiuneId = r.data.versiune_id;
        agg.versiune_id = r.data.versiune_id;
        agg.inserate += r.data.inserate;
        agg.duplicate += r.data.duplicate;
        agg.nepotrivite += r.data.nepotrivite;
        agg.randuri_nepotrivite.push(...r.data.randuri_nepotrivite);
        agg.adaugate = r.data.adaugate;
        agg.eliminate = r.data.eliminate;
        agg.neschimbate = r.data.neschimbate;
        setProgres({
          trimise: Math.min((i + 1) * LOT, randuri.length),
          total: randuri.length,
        });
      }
      setRezultat(agg);
    } catch (e) {
      setEroare(e instanceof Error ? e.message : "Importul a eșuat");
    } finally {
      setProgres(null);
    }
  };

  return (
    <Dialog open={deschis} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="flex max-h-[92vh] w-[96vw] max-w-[1100px] flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle className="font-heading">
            {listaExistenta
              ? `Actualizează lista „${listaExistenta.nume_fisier}”`
              : "Import listă"}
          </DialogTitle>
          <DialogDescription>
            Încarcă un fișier (Excel, CSV, PDF, Word, HTML), preia lista direct
            de la sursă sau lipește text. Firmele se potrivesc după CUI, număr
            de înmatriculare sau denumire exactă; rândurile fără firmă rămân în
            raportul de import. La înlocuire se calculează diferențele față de
            versiunea anterioară.
          </DialogDescription>
        </DialogHeader>

        <div className="min-h-0 flex-1 space-y-4 overflow-auto pr-1">
          {rezultat ? (
            <div className="space-y-3 rounded-md border p-4">
              <p className="font-medium">Import finalizat</p>
              <dl className="grid grid-cols-2 gap-2 text-sm md:grid-cols-4">
                {[
                  ["Rânduri trimise", randuri.length],
                  ["Firme inserate", rezultat.inserate],
                  ["Duplicate (deja pe listă)", rezultat.duplicate],
                  ["Fără firmă potrivită", rezultat.nepotrivite],
                  ["Adăugate față de versiunea anterioară", rezultat.adaugate],
                  [
                    "Eliminate față de versiunea anterioară",
                    rezultat.eliminate,
                  ],
                  ["Neschimbate", rezultat.neschimbate],
                ].map(([k, v]) => (
                  <div key={String(k)}>
                    <dt className="text-xs text-muted-foreground">{k}</dt>
                    <dd className="font-semibold tabular-nums">
                      {formatNumar(Number(v))}
                    </dd>
                  </div>
                ))}
              </dl>
              <div className="flex flex-wrap gap-2">
                {rezultat.randuri_nepotrivite.length > 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      descarcaFisierText(
                        csvNepotrivite(rezultat.randuri_nepotrivite),
                        `nepotrivite-${numeFinal.replace(/[^a-z0-9]+/gi, "-")}.csv`
                      )
                    }
                  >
                    <Download className="mr-2 h-4 w-4" />
                    Descarcă rândurile fără firmă (
                    {formatNumar(rezultat.randuri_nepotrivite.length)})
                  </Button>
                )}
                {onVersiune && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onVersiune(rezultat.versiune_id)}
                  >
                    Vezi diferențele acestei versiuni
                  </Button>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                Firmele nou adăugate intră în încadrare la următoarea rulare a
                reîncadrării; potrivirea listei a fost deja reîmprospătată.
              </p>
            </div>
          ) : (
            <>
              <Tabs value={sursa} onValueChange={(v) => setSursa(v as Sursa)}>
                <TabsList>
                  <TabsTrigger value="fisier">Fișier</TabsTrigger>
                  <TabsTrigger value="url">Adresă web (sursa)</TabsTrigger>
                  <TabsTrigger value="text">Text lipit</TabsTrigger>
                </TabsList>
              </Tabs>
              {sursa === "fisier" && (
                <Input
                  type="file"
                  accept=".csv,.tsv,.txt,.xlsx,.xls,.xlsm,.pdf,.html,.htm,.docx"
                  onChange={(e) => void alegeFisier(e.target.files?.[0])}
                />
              )}
              {sursa === "url" && (
                <div className="space-y-1">
                  <div className="flex gap-2">
                    <Input
                      value={url}
                      onChange={(e) => setUrl(e.target.value)}
                      placeholder="https://… (pagina cu tabelul sau fișierul Excel/CSV/PDF)"
                    />
                    <Button
                      variant="outline"
                      onClick={preiaUrl}
                      disabled={preia.isPending || !url.trim()}
                    >
                      {preia.isPending ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : null}
                      Preia
                    </Button>
                  </div>
                  {metodaSursa === "POST" && (
                    <p className="text-xs text-muted-foreground">
                      Se preia prin formularul de export al sursei (POST), cu
                      token-ul din pagina {initial?.pagina_token}.
                    </p>
                  )}
                </div>
              )}
              {sursa === "text" && (
                <div className="space-y-2">
                  <Textarea
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    rows={6}
                    placeholder="Lipește un tabel (CSV, valori separate prin ; sau tab) sau text liber cu firme și CUI-uri"
                  />
                  <div className="flex items-center gap-3">
                    <label className="flex items-center gap-2 text-sm">
                      <Checkbox
                        checked={extras}
                        onCheckedChange={(v) => setExtras(v === true)}
                      />
                      Extrage CUI-uri și denumiri din text liber
                    </label>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={foloseste}
                      disabled={!text.trim()}
                    >
                      Folosește textul
                    </Button>
                  </div>
                </div>
              )}
              {parsare && (
                <p className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" /> Se citește
                  fișierul…
                </p>
              )}
              {eroare && (
                <p className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
                  {eroare}
                </p>
              )}

              {tabel && incarcat && (
                <div className="space-y-3 rounded-md border p-3">
                  <div className="flex flex-wrap items-center gap-3 text-sm">
                    <span>
                      <span className="text-muted-foreground">Fișier: </span>
                      {incarcat.nume} · {formatNumar(tabel.randuri.length)}{" "}
                      rânduri
                      {tabel.mod === "extras" && " (extrase din text)"}
                    </span>
                    {tabel.foi && tabel.foi.length > 1 && (
                      <Select
                        value={foaie ?? tabel.foi[0]}
                        onValueChange={(f) => void schimbaFoaia(f)}
                      >
                        <SelectTrigger className="h-8 w-[220px] text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {tabel.foi.map((f) => (
                            <SelectItem key={f} value={f}>
                              foaia „{f}”
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                    {(incarcat.tip === "html" || incarcat.tip === "text") && (
                      <label className="flex items-center gap-2 text-xs">
                        <Checkbox
                          checked={extras}
                          onCheckedChange={(v) =>
                            void schimbaExtras(v === true)
                          }
                        />
                        Extrage CUI-uri din text în loc de tabel
                      </label>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-3">
                    <SelectColoana
                      eticheta="Coloana CUI"
                      antet={tabel.antet}
                      valoare={mapare.cui}
                      onChange={(i) => setMapare({ ...mapare, cui: i })}
                    />
                    <SelectColoana
                      eticheta="Coloana denumire"
                      antet={tabel.antet}
                      valoare={mapare.denumire}
                      onChange={(i) => setMapare({ ...mapare, denumire: i })}
                    />
                    <SelectColoana
                      eticheta="Coloana nr. înmatriculare"
                      antet={tabel.antet}
                      valoare={mapare.cod_inmatriculare}
                      onChange={(i) =>
                        setMapare({ ...mapare, cod_inmatriculare: i })
                      }
                    />
                    <SelectColoana
                      eticheta="Coloana care determină serviciul (opțional)"
                      antet={tabel.antet}
                      valoare={coloanaServiciu}
                      onChange={setColoanaServiciu}
                    />
                    <p className="self-end text-xs text-muted-foreground">
                      {formatNumar(randuri.length)} rânduri utile,{" "}
                      {formatNumar(cuCui)} cu CUI
                      {mapareActiva &&
                        `, ${formatNumar(faraServiciu)} fără serviciu (se omit)`}
                    </p>
                  </div>
                  {coloanaServiciu !== null && preaMulteValori && (
                    <p className="text-xs text-destructive">
                      Coloana are {formatNumar(valoriServiciu.length)} valori
                      distincte; alege o coloană cu cel mult{" "}
                      {MAX_VALORI_SERVICIU}.
                    </p>
                  )}
                  {mapareActiva && (
                    <div className="space-y-1 rounded border p-2">
                      <p className="text-xs font-medium">
                        Serviciul pentru fiecare valoare din coloana „
                        {tabel.antet[coloanaServiciu ?? 0]}”
                      </p>
                      <div className="grid gap-1 md:grid-cols-2">
                        {valoriServiciu.map((v) => (
                          <div
                            key={v}
                            className="flex items-center gap-2 text-xs"
                          >
                            <span
                              className="w-[220px] shrink-0 truncate"
                              title={v}
                            >
                              {v}
                            </span>
                            <Select
                              value={mapareServiciu[v] ?? FARA_SERVICIU}
                              onValueChange={(id) =>
                                setMapareServiciu((m) => {
                                  const n = { ...m };
                                  if (id === FARA_SERVICIU) delete n[v];
                                  else n[v] = id;
                                  return n;
                                })
                              }
                            >
                              <SelectTrigger className="h-7 text-xs">
                                <SelectValue placeholder="(se omite)" />
                              </SelectTrigger>
                              <SelectContent className="max-h-72">
                                <SelectItem value={FARA_SERVICIU}>
                                  (se omite)
                                </SelectItem>
                                {servicii.map((s) => (
                                  <SelectItem
                                    key={s.entity_id}
                                    value={s.entity_id}
                                  >
                                    {s.entity_id} · {s.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="max-h-56 overflow-auto rounded border">
                    <table className="w-full text-xs">
                      <thead className="sticky top-0 bg-muted/60">
                        <tr>
                          {tabel.antet.map((h, i) => (
                            <th
                              key={`${i}-${h}`}
                              className="whitespace-nowrap px-2 py-1 text-left font-medium"
                            >
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {tabel.randuri.slice(0, 15).map((r, i) => (
                          <tr key={i} className="border-t">
                            {tabel.antet.map((_, j) => (
                              <td
                                key={j}
                                className="max-w-[240px] truncate px-2 py-1"
                                title={r[j]}
                              >
                                {r[j]}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {tabel && (
                <div className="grid gap-3 rounded-md border p-3 md:grid-cols-2">
                  <label className="space-y-1 text-xs">
                    <span className="text-muted-foreground">
                      Lista de destinație
                    </span>
                    <Select value={listaTinta} onValueChange={setListaTinta}>
                      <SelectTrigger className="h-8 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="max-h-80">
                        <SelectItem value={LISTA_NOUA}>Listă nouă</SelectItem>
                        {fisiere.map((f) => (
                          <SelectItem key={f.nume_fisier} value={f.nume_fisier}>
                            {f.nume_fisier}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </label>
                  {listaTinta === LISTA_NOUA && (
                    <label className="space-y-1 text-xs">
                      <span className="text-muted-foreground">
                        Numele listei noi
                      </span>
                      <Input
                        value={numeNou}
                        onChange={(e) => setNumeNou(e.target.value)}
                        className="h-8 text-xs"
                      />
                    </label>
                  )}
                  <label className="space-y-1 text-xs">
                    <span className="text-muted-foreground">
                      Serviciul NIS2 al listei
                      {mapareActiva && " (înlocuit de maparea pe coloană)"}
                    </span>
                    <SelectServiciu
                      valoare={serviceId}
                      servicii={servicii}
                      onChange={setServiceId}
                      latime="w-full"
                      disabled={mapareActiva}
                    />
                  </label>
                  <label className="space-y-1 text-xs">
                    <span className="text-muted-foreground">Tipul listei</span>
                    <Select
                      value={tip}
                      onValueChange={(t) => setTip(t as TargetareTipLista)}
                    >
                      <SelectTrigger className="h-8 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {(
                          ["OFICIALA", "NEOFICIALA"] as TargetareTipLista[]
                        ).map((t) => (
                          <SelectItem key={t} value={t}>
                            {ETICHETE_TIP_LISTA[t]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </label>
                  <label className="flex items-center gap-2 text-sm md:col-span-2">
                    <Checkbox
                      checked={inlocuieste}
                      onCheckedChange={(v) => setInlocuieste(v === true)}
                    />
                    Înlocuiește conținutul listei cu acest import și calculează
                    diferențele (altfel rândurile noi se adaugă la cele
                    existente)
                  </label>
                </div>
              )}

              {progres && (
                <div className="space-y-1">
                  <Progress
                    value={
                      progres.total === 0
                        ? 100
                        : (progres.trimise / progres.total) * 100
                    }
                  />
                  <p className="text-xs text-muted-foreground">
                    {formatNumar(progres.trimise)} /{" "}
                    {formatNumar(progres.total)} rânduri trimise
                  </p>
                </div>
              )}
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {rezultat ? "Închide" : "Renunță"}
          </Button>
          {!rezultat && (
            <Button onClick={() => void ruleaza()} disabled={!poateImporta}>
              {progres ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Upload className="mr-2 h-4 w-4" />
              )}
              Importă{" "}
              {randuri.length > 0
                ? `${formatNumar(randuri.length)} rânduri`
                : ""}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
